import { useEffect, useRef, useState, useCallback } from "react";

/**
 * V1.9.1 — useDraggable
 *
 * Makes an element draggable by a "handle" sub-element. Position is
 * stored as an `{x, y}` offset (delta from the element's CSS anchor)
 * and persisted to localStorage under the supplied key so user-chosen
 * positions survive reloads.
 *
 * Usage:
 *   const { handleProps, containerProps, reset } = useDraggable("preview-pdf-btn");
 *   return (
 *     <div className="absolute top-4 right-4" {...containerProps}>
 *       <button {...handleProps}>Drag me</button>
 *     </div>
 *   );
 *
 * - Pointer events used (works for touch + mouse + pen).
 * - The drag is opt-in via the handle — the user only gets dragging
 *   from a known grip area, so internal interactive children (buttons,
 *   inputs) keep their own click semantics.
 * - The hook clamps positions to the viewport on resize so a button
 *   that was dragged off-screen on desktop doesn't get lost on mobile.
 *
 * Returns:
 *   containerProps: { ref, style }     spread on the OUTER positioned element
 *   handleProps:    { ref, onPointerDown, style: { cursor } }
 *   reset()                            reset to default origin (clears storage)
 *   position                           current { x, y } offset
 *   dragging                           true while user is actively dragging
 */
export default function useDraggable(storageKey, defaultPos = { x: 0, y: 0 }) {
  const containerRef = useRef(null);
  const handleRef = useRef(null);
  const dragState = useRef(null); // { startX, startY, originX, originY }
  const [position, setPosition] = useState(() => {
    if (typeof window === "undefined") return defaultPos;
    try {
      const raw = localStorage.getItem(`draggable_${storageKey}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (
          parsed &&
          typeof parsed.x === "number" &&
          typeof parsed.y === "number"
        ) {
          return parsed;
        }
      }
    } catch {
      // bad json - fall through to default
    }
    return defaultPos;
  });
  const [dragging, setDragging] = useState(false);

  // Persist on change (debounced via storage key — overwrites are cheap).
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(
        `draggable_${storageKey}`,
        JSON.stringify(position),
      );
    } catch {
      // storage quota - ignore.
    }
  }, [position, storageKey]);

  // Keep button inside the viewport on resize. If the element ends up off-
  // screen (e.g. mobile rotation), nudge it back into view.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleResize = () => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let { x, y } = position;
      let changed = false;
      const minVisible = 40; // keep at least 40px of the element on-screen
      if (rect.right < minVisible) {
        x += minVisible - rect.right;
        changed = true;
      } else if (rect.left > vw - minVisible) {
        x -= rect.left - (vw - minVisible);
        changed = true;
      }
      if (rect.bottom < minVisible) {
        y += minVisible - rect.bottom;
        changed = true;
      } else if (rect.top > vh - minVisible) {
        y -= rect.top - (vh - minVisible);
        changed = true;
      }
      if (changed) setPosition({ x, y });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [position]);

  const handlePointerMove = useCallback((e) => {
    if (!dragState.current) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    setPosition({
      x: dragState.current.originX + dx,
      y: dragState.current.originY + dy,
    });
  }, []);

  const handlePointerUp = useCallback(() => {
    if (!dragState.current) return;
    dragState.current = null;
    setDragging(false);
    window.removeEventListener("pointermove", handlePointerMove);
    window.removeEventListener("pointerup", handlePointerUp);
    window.removeEventListener("pointercancel", handlePointerUp);
  }, [handlePointerMove]);

  const handlePointerDown = useCallback(
    (e) => {
      // Only left-button / primary touch starts a drag.
      if (e.button !== undefined && e.button !== 0) return;
      e.preventDefault();
      dragState.current = {
        startX: e.clientX,
        startY: e.clientY,
        originX: position.x,
        originY: position.y,
      };
      setDragging(true);
      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
      window.addEventListener("pointercancel", handlePointerUp);
    },
    [position, handlePointerMove, handlePointerUp],
  );

  const reset = useCallback(() => {
    setPosition(defaultPos);
    try {
      localStorage.removeItem(`draggable_${storageKey}`);
    } catch {
      // ignore
    }
  }, [defaultPos, storageKey]);

  // Clean up listeners if the consumer unmounts mid-drag.
  useEffect(() => {
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [handlePointerMove, handlePointerUp]);

  return {
    containerProps: {
      ref: containerRef,
      style: {
        transform: `translate3d(${position.x}px, ${position.y}px, 0)`,
        touchAction: "none",
      },
    },
    handleProps: {
      ref: handleRef,
      onPointerDown: handlePointerDown,
      style: { cursor: dragging ? "grabbing" : "grab", touchAction: "none" },
    },
    reset,
    position,
    dragging,
  };
}
